import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { 
  School, BookOpen, Bookmark, Users, ChevronLeft, 
  PlusCircle, FileText, User, Mail, Phone, MapPin, 
  Calendar, Clock, Search, Download, Filter,Edit2, Trash2,Eye
} from 'lucide-react';
import api from '../components/api';
import { toast } from 'react-toastify';
import ConfirmationModal from '../components/ConfirmationModal';
import { getDeleteConfirmation } from '../utils/confirmationUtils';


const DetailClasse = () => {
  const { classeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [classeDetails, setClasseDetails] = useState(null);
  const [etudiants, setEtudiants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('tous');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [etudiantToDelete, setEtudiantToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchClasseDetails();
  }, [classeId]);

  const fetchClasseDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/classe/effectif-classe/${classeId}/`);
      setClasseDetails(response.data.classe);
      setEtudiants(response.data.etudiants || []);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des détails de la classe:', error);
      toast.error('Erreur lors du chargement des détails de la classe');
      setLoading(false);
      // Rediriger vers la liste des classes en cas d'erreur
      navigate('/classes');
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
  };

  const handleOpenDeleteModal = (etudiant) => {
    setEtudiantToDelete(etudiant);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteEtudiant = async () => {
    if (!etudiantToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/eleve/details-etudiant/${etudiantToDelete.id}/`);
      toast.success('Étudiant retiré de la classe avec succès');
      fetchClasseDetails(); // Rafraîchir les données
      setIsDeleteModalOpen(false);
      setEtudiantToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'étudiant:', error);
      toast.error('Erreur lors de la suppression de l\'étudiant');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAddStudent = () => {
    navigate(`/ajouter-etudiant/${classeId}`);
  };

  const handleGenerateReport = () => {
    toast.info('Génération du rapport en cours...');
    // Implémenter la génération de rapport ici
  };

  const handleBackToClasses = () => {
    navigate('/classes');
  };

  const filteredEtudiants = etudiants.filter(etudiant => {
    const matchesSearch = 
      etudiant.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      etudiant.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      etudiant.matricule?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (selectedFilter === 'tous') return matchesSearch;
    return matchesSearch && etudiant.genre === selectedFilter;
  });

  // Configuration pour la modale de confirmation
  const confirmConfig = etudiantToDelete 
    ? getDeleteConfirmation(etudiantToDelete, 'l\'étudiant', 'nom', 'retirer') 
    : {};

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#202654]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* En-tête avec détails de la classe */}
        <div className="mb-6">
          <button 
            onClick={handleBackToClasses}
            className="flex items-center text-sm text-gray-600 hover:text-[#202654] mb-4 transition-colors"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Retour à la liste des classes
          </button>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center mb-4 md:mb-0">
                  <div className="h-12 w-12 rounded-full bg-[#202654]/10 flex items-center justify-center mr-4">
                    <School className="h-6 w-6 text-[#202654]" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">{classeDetails?.libelle}</h1>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      {classeDetails?.niveau && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <Bookmark className="h-3.5 w-3.5 mr-1" />
                          {classeDetails.niveau.libelle}
                        </span>
                      )}
                      {classeDetails?.filiere && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <BookOpen className="h-3.5 w-3.5 mr-1" />
                          {classeDetails.filiere.libelle}
                        </span>
                      )}
                      {classeDetails?.annee && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          classeDetails.annee.status 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          <Calendar className="h-3.5 w-3.5 mr-1" />
                          {classeDetails.annee.libelle}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleAddStudent}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none"
                  >
                    <PlusCircle className="h-4 w-4 mr-2" />
                    Ajouter un étudiant
                  </button>
                  
                  <button
                    onClick={handleGenerateReport}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Générer rapport
                  </button>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-6 bg-gray-50 border-t border-gray-200">
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                    <Users className="h-5 w-5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">Effectif total</p>
                    <p className="text-lg font-semibold text-gray-900">{etudiants.length} étudiants</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-green-100 text-green-600">
                    <User className="h-5 w-5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">Garçons</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {etudiants.filter(e => e.genre === 'M').length} étudiants
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-pink-100 text-pink-600">
                    <User className="h-5 w-5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">Filles</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {etudiants.filter(e => e.genre === 'F').length} étudiantes
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-amber-100 text-amber-600">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">Dernier mise à jour</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {classeDetails?.update_at 
                        ? new Date(classeDetails.update_at).toLocaleDateString() 
                        : 'Non défini'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Liste des étudiants */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h2 className="text-lg font-semibold text-gray-900">Liste des étudiants</h2>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Rechercher un étudiant..."
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-[#202654] focus:border-[#202654]"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
                
                <div className="flex items-center">
                  <Filter className="h-5 w-5 text-gray-400 mr-2" />
                  <select
                    className="border border-gray-300 rounded-lg py-2 px-3 focus:ring-[#202654] focus:border-[#202654]"
                    value={selectedFilter}
                    onChange={(e) => handleFilterChange(e.target.value)}
                  >
                    <option value="tous">Tous</option>
                    <option value="M">Garçons</option>
                    <option value="F">Filles</option>
                  </select>
                </div>
                
                <button
                  onClick={() => toast.info('Téléchargement de la liste en cours...')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exporter
                </button>
              </div>
            </div>
          </div>
          
          {filteredEtudiants.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun étudiant trouvé</h3>
              <p className="text-sm text-gray-500 max-w-md text-center mb-6">
                {searchTerm 
                  ? 'Aucun étudiant ne correspond à votre recherche' 
                  : 'Cette classe ne contient pas encore d\'étudiants'}
              </p>
              <button
                onClick={handleAddStudent}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Ajouter un étudiant
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Étudiant
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Matricule
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Genre
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEtudiants.map((etudiant) => (
                    <tr key={etudiant.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`h-9 w-9 rounded-full flex items-center justify-center mr-3 ${
                            etudiant.genre === 'M' ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600'
                          }`}>
                            <User className="h-5 w-5" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {etudiant.nom} {etudiant.prenom}
                            </div>
                            <div className="text-xs text-gray-500">
                              {etudiant.date_naissance && new Date(etudiant.date_naissance).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {etudiant.matricule || 'Non défini'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          {etudiant.email && (
                            <div className="flex items-center text-xs text-gray-500 mb-1">
                              <Mail className="h-3.5 w-3.5 mr-1 text-gray-400" />
                              {etudiant.email}
                            </div>
                          )}
                          {etudiant.telephone && (
                            <div className="flex items-center text-xs text-gray-500">
                              <Phone className="h-3.5 w-3.5 mr-1 text-gray-400" />
                              {etudiant.telephone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          etudiant.genre === 'M' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-pink-100 text-pink-800'
                        }`}>
                          {etudiant.genre === 'M' ? 'Garçon' : 'Fille'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => navigate(`/etudiant/${etudiant.id}`)}
                          className="text-blue-600 hover:text-blue-900 mr-3 p-1 rounded-full hover:bg-blue-50 transition-colors"
                          title="Voir le profil"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => navigate(`/modifier-etudiant/${etudiant.id}`)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3 p-1 rounded-full hover:bg-indigo-50 transition-colors"
                          title="Modifier"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenDeleteModal(etudiant)}
                          className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                          title="Retirer de la classe"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmation pour la suppression d'un étudiant */}
      <ConfirmationModal 
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteEtudiant}
        title={confirmConfig.title || "Retirer l'étudiant"}
        message={confirmConfig.message || ""}
        confirmText={confirmConfig.confirmText || "Retirer"}
        cancelText="Annuler"
        type={confirmConfig.type || "danger"}
        isLoading={deleteLoading}
      />
    </Layout>
  );
};

export default DetailClasse;
